import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'

export function isUnifiedRateAccommodationOffer(offer: App.HotelOffer | App.TourOffer | App.VillaOffer): offer is App.HotelOffer {
  return offer.type === 'tactical_ao_hotel'
}

/**
 * Ticket inclusions of the opt-in bundle(s) a parent item belongs to, read off the checkout-fetched
 * property rate — the same source mandatory bundles use for `bundleInclusions`. Returns undefined
 * when the rate isn't available (rates still fetching) or no longer carries any of the bundles.
 */
export function getOptInBundleInclusionsFromRate(
  rate: { optionalBundles?: Array<App.Property.OptionalBundle> } | undefined,
  item: { accommBundleIds?: Array<string> },
): Array<App.PackageInclusion> | undefined {
  const bundleIds = item.accommBundleIds
  if (!bundleIds?.length) return undefined
  const inclusions = bundleIds
    .flatMap(bundleId => rate?.optionalBundles?.find(bundle => bundle.bundleId === bundleId)?.tickets ?? [])
    .map(({ inclusion }) => ({ id: inclusion.id, description: inclusion.description, symbol: inclusion.symbol }))
    .filter(inclusion => !!inclusion.description)
  return inclusions.length ? inclusions : undefined
}

// NB: You can use the returned `RawPropertyRate` for inclusions and bundle information, but you
// mustn't use it for pricing, as the raw rates stored in state have not been enriched with flights.
export function findUnifiedRateForCheckoutItem(
  item: App.Checkout.LEAccommodationItem,
  offer: App.HotelOffer,
  propertyRatesByKey: App.OfferState['propertyRates'] | undefined,
): App.RawPropertyRate | undefined {
  if (!isInstantBookingLEHotelItem(item) || !propertyRatesByKey) {
    return undefined
  }

  const allRates = Object.values(propertyRatesByKey).flatMap(state =>
    (state?.rooms ?? [])
      .filter((entry): entry is App.RawAvailablePropertyRoomRates => 'rates' in entry)
      .flatMap(entry => Object.values(entry.rates)),
  )

  const ref = item.propertyRateRef
  if (ref?.serviceType === 'lpc') {
    const refPropertyRates = allRates.filter(rate =>
      rate.service.type === 'lpc' &&
      rate.propertyId === ref.propertyId &&
      rate.roomId === ref.roomId,
    )

    if (refPropertyRates.length) {
      const scopedRates = ref.bundleId
        ? refPropertyRates.filter(rate => rate.bundle?.bundleId === ref.bundleId)
        : refPropertyRates

      if (ref.groupKey) {
        const byGroupKey = scopedRates.find(rate => rate.groupKey === ref.groupKey)
        if (byGroupKey) return byGroupKey
      }

      const byRateId = scopedRates.find(rate => rate.rateId === ref.serviceRateId || rate.service.rateId === ref.serviceRateId)
      if (byRateId) return byRateId
    }
  }

  const propertyRates = allRates.filter(rate => rate.propertyId === offer.property?.id)
  return propertyRates.find(rate => rate.rateId === item.roomRateId || rate.service.rateId === item.roomRateId)
}
